import type { BoxProps } from '@shopify/polaris';

import { BlockStack, Box, InlineStack, List, Popover, Text } from '@shopify/polaris';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { cls } from '~/utils/cls';

type CloseTarget = 'activator' | 'next-node';
export interface PopoverPublicAPI {
  forceUpdatePosition(): void;
  close(target?: CloseTarget): void;
}

export interface GTooltipCardPublicAPI {
  onClose(): void;
}

type Width = 'default' | 'wide' | 'large';

export interface GTooltipCardData {
  title: string;
  content: string | TrustedHTML;
  formula?: React.ReactNode;
  contentList?: string[];
  width?: Width;
}

export interface GTooltipCardProps {
  children: React.ReactNode;
  /**
   * Delay in `ms` before the tooltip is shown
   * @default 0 (no delay)
   */
  delay?: number;
  tooltip?: GTooltipCardData;
  activatorProps?: BoxProps;
  wrapper?: 'span' | 'div';
  isHideBorder?: boolean;
  alignment?: 'start' | 'center' | 'end';
  textDecoration?: 'underline' | 'none';
  preferredAlignment?: 'left' | 'center' | 'right';
  popoverRef?: React.RefObject<PopoverPublicAPI>;
  /** Show arrow pointing to activator. @default false */
  showArrow?: boolean;
}

const SIZE_CLASS: Record<Width, string> = {
  default: 'w-[210px]',
  wide: 'w-[320px]',
  large: 'w-[328px]',
};

const ALIGNMENT_MAP = {
  start: 'flex justify-start',
  center: 'flex justify-center',
  end: 'flex justify-end',
};

export const GTooltipCard = forwardRef<GTooltipCardPublicAPI, GTooltipCardProps>((props, ref) => {
  const {
    tooltip,
    wrapper = 'div',
    activatorProps,
    isHideBorder,
    alignment,
    delay = 500,
    textDecoration = 'none',
    popoverRef,
    preferredAlignment = 'left',
    showArrow = false,
  } = props;
  const { width = 'default' } = tooltip ?? {};

  const [isMouseEnter, setIsMouseEnter] = useState(false);

  const TooltipCardWrapper = wrapper;

  const sizeClass = SIZE_CLASS[width];

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsMouseEnter(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsMouseEnter(false);
  };

  useImperativeHandle(ref, () => ({ onClose: handleMouseLeave }));

  return (
    <TooltipCardWrapper
      className={cls('GTooltipCard cursor-pointer ', alignment && ALIGNMENT_MAP[alignment], {
        'GTooltipCard--text-underline': textDecoration === 'underline',
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Popover
        ref={popoverRef}
        activator={
          !isHideBorder ? (
            <Box
              borderBlockEndWidth="025"
              borderStyle="dashed"
              borderColor="border-tertiary"
              as={wrapper}
              {...activatorProps}
            >
              {props.children}
            </Box>
          ) : (
            <InlineStack>{props.children}</InlineStack>
          )
        }
        activatorWrapper={wrapper}
        onClose={() => {}}
        active={isMouseEnter}
        preferredPosition="below"
        preferredAlignment={preferredAlignment}
      >
        {tooltip && (
          <div className={cls(sizeClass, { 'GTooltipCard-arrow': showArrow })}>
            <Box padding="400">
              <BlockStack gap="200">
                <BlockStack gap="100">
                  <Text as="span" variant="headingSm" fontWeight="semibold">
                    {tooltip.title}
                  </Text>
                  <BlockStack gap="400">
                    <Text as="span" variant="bodyMd" tone="subdued" fontWeight="medium">
                      <span dangerouslySetInnerHTML={{ __html: tooltip.content }}></span>
                    </Text>
                    {tooltip.contentList && (
                      <List type="bullet">
                        {tooltip.contentList.map((item) => (
                          <List.Item key={item}>
                            <Text as="span" variant="bodyMd" tone="subdued" fontWeight="medium">
                              {item}
                            </Text>
                          </List.Item>
                        ))}
                      </List>
                    )}
                  </BlockStack>
                </BlockStack>

                {tooltip.formula && (
                  <div className="rounded-md font-mono !text-bodySm">{tooltip.formula}</div>
                )}
              </BlockStack>
            </Box>
          </div>
        )}
      </Popover>
    </TooltipCardWrapper>
  );
});

GTooltipCard.displayName = 'GTooltipCard';
